import React, { useEffect, useRef } from "react";

import { CrossIcon } from "@/assets/icons";
import { cn } from "@/lib/cn";

import { ActionButton } from "./ActionButton";
import { DeleteAction } from "./DeleteAction";
import { EditAction } from "./EditAction";
import { MoreActions } from "./MoreActions";

const SidePanelViewLayout = (props: {
  children?: React.ReactNode;
  className?: string;
  /**
   * Names what the panel shows next to its title, e.g. the person once their name has scrolled out
   * of sight. Visual only: the content already says it to a screen reader. Under 1200px it takes
   * the title's place on screen, so a long name and the status still fit a narrow panel.
   */
  context?: React.ReactNode;
  /**
   * A live state of the panel, e.g. "Editing · 2 changes", kept in the header whatever the scroll.
   * Unlike `context` it is read out when it changes. Opt-in: pass `""` while there is nothing to
   * say, so the live region exists before its first text; leave it undefined for no region at all.
   */
  status?: React.ReactNode;
  /** Closes the panel on Esc pressed inside it; pass false while closing would discard edits. */
  closeOnEscape?: boolean;
  displayFooter?: boolean;
  /** Moves focus to the panel title on mount and whenever the key changes, e.g. the shown item's id. */
  focusKey?: string;
  id?: string;
  isVisible?: boolean;
  title: string;
  /**
   * The level of the title. The default `h2` suits a panel whose content opens with its own
   * headings; a panel whose subject is named by a heading of its own (the person, the team) sets
   * `p`, so the outline has one `h2`: the subject. Fixed per panel: switching it remounts the
   * title and loses the focus `focusKey` placed there.
   */
  titleAs?: "h2" | "p";
  /**
   * The id of an element inside the panel that names it, e.g. the subject's heading, instead of the
   * title. Pass undefined while that element is not rendered: the title names the panel then.
   */
  labelledBy?: string;
  onClose: () => void;
  renderActions?: () => React.ReactNode;
  renderFooter?: () => React.ReactNode;
}) => {
  const {
    children,
    className,
    closeOnEscape = false,
    context,
    displayFooter = false,
    focusKey,
    id,
    title,
    isVisible,
    labelledBy,
    onClose,
    renderActions,
    renderFooter,
    status,
    titleAs: TitleTag = "h2",
  } = props;
  const cnPrefix = className ? `${className}-` : "";
  const titleRef = useRef<HTMLHeadingElement & HTMLParagraphElement>(null);

  useEffect(() => {
    if (focusKey !== undefined) {
      titleRef.current?.focus();
    }
  }, [focusKey]);

  // A menu inside the panel marks the Esc that closes it as handled; one in a portal is not the panel's
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (
      closeOnEscape &&
      e.key === "Escape" &&
      !e.defaultPrevented &&
      e.currentTarget.contains(e.target as Node)
    ) {
      onClose();
    }
  };

  return (
    <article
      id={id}
      className={cn(
        "flex h-full flex-col bg-background",
        !(isVisible ?? true) && "hidden",
        className,
      )}
      aria-labelledby={labelledBy ?? (id ? `${id}-title` : undefined)}
      onKeyDown={handleKeyDown}
    >
      <header
        className={`${cnPrefix}header flex items-center justify-between gap-3 border-b border-border px-4 py-3 min-[1200px]:gap-6 min-[1200px]:px-6 min-[1200px]:py-4`}
      >
        <div className="flex min-w-0 flex-1 items-baseline gap-2">
          <TitleTag
            id={id ? `${id}-title` : undefined}
            ref={titleRef}
            tabIndex={-1}
            className={cn(
              "m-0 shrink-0 text-xs font-medium text-muted-foreground focus:outline-hidden",
              // In a panel under 1200px wide the room goes to what the context names (the person) and
              // to the status; the title stays for assistive technology and for focus
              context && "max-[1199px]:sr-only",
            )}
          >
            {title}
          </TitleTag>
          {context && (
            <span aria-hidden className="truncate text-sm font-semibold text-foreground">
              {context}
            </span>
          )}
        </div>
        {/* Kept in the document while the caller opts in, so a screen reader hears the text appear */}
        {status !== undefined && (
          <span
            role="status"
            className={cn(
              status
                ? "shrink-0 whitespace-nowrap text-xs font-medium tabular-nums text-primary-fg"
                : "sr-only",
            )}
          >
            {status}
          </span>
        )}
        <div className="flex h-full items-center gap-3 pointer-coarse:gap-1">
          {renderActions && renderActions()}
          <ActionButton
            aria-label="Close panel"
            title="Close panel"
            onClick={onClose}
            className="relative ml-3 before:absolute before:-left-3 before:block before:h-full before:w-px before:bg-border before:content-[''] [&_svg]:w-3.5"
          >
            <CrossIcon />
          </ActionButton>
        </div>
      </header>
      <section
        className={`${cnPrefix}content flex flex-1 flex-col gap-8 overflow-auto p-4 min-[1200px]:px-6 min-[1200px]:py-8`}
      >
        {children}
      </section>
      {displayFooter && !!renderFooter && (
        <footer className="flex items-center justify-end gap-3 border-t border-border px-4 py-3 min-[1200px]:px-6 min-[1200px]:py-4">
          {renderFooter()}
        </footer>
      )}
    </article>
  );
};

SidePanelViewLayout.DeleteAction = DeleteAction;
SidePanelViewLayout.EditAction = EditAction;
SidePanelViewLayout.MoreActions = MoreActions;

export { SidePanelViewLayout };
